import api from './axios'
import { io } from 'socket.io-client'

class NotificationService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.callbacks = {
      newNotification: [],
      unreadCount: [],
      connected: [],
      disconnected: [],
      error: []
    }
  }

  // Initialize WebSocket connection
  async connect(token) {
    try {
      if (this.socket) {
        this.disconnect()
      }

      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000'
      // Remove /api suffix if present and add /notifications namespace
      const socketURL = baseURL.replace('/api', '')

      // Connect to WebSocket server
      this.socket = io(socketURL, {
        path: '/socket.io/',
        transports: ['polling', 'websocket'],
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        timeout: 20000
      })

      // Setup event handlers
      this.setupEventHandlers()

      // Connect and authenticate
      this.socket.connect()
      this.socket.emit('authenticate', { token })

      console.log('🔌 Attempting to connect to notification service at:', socketURL)

    } catch (error) {
      console.error('Error connecting to notification service:', error)
      this.emit('error', error)
    }
  }

  setupEventHandlers() {
    this.socket.on('connect', () => {
      console.log('✅ Connected to notification service')
      this.isConnected = true
      this.reconnectAttempts = 0
      this.emit('connected')
    })

    this.socket.on('authenticated', (data) => {
      console.log('✅ Authenticated with notification service:', data.userId)
      // Start ping interval to keep connection alive
      this.startPingInterval()
    })

    this.socket.on('auth_error', (error) => {
      console.error('❌ Authentication failed:', error.message)
      this.emit('error', new Error(error.message))
    })

    this.socket.on('new_notification', (notification) => {
      console.log('📨 New notification received:', notification)
      this.emit('newNotification', notification)

      // Show browser notification if permission granted
      this.showBrowserNotification(notification)
    })

    this.socket.on('leave_update', (data) => {
      console.log('🔄 Leave update received:', data)
      this.emit('leaveUpdate', data)

      // Trigger calendar refresh
      window.dispatchEvent(new CustomEvent('calendar-refresh', { detail: data }))

      // Trigger inbox refresh if this is a manager/HR update
      if (data.payload && data.payload.data) {
        const leaveData = data.payload.data
        window.dispatchEvent(new CustomEvent('inbox-refresh', { detail: leaveData }))
      }
    })

    this.socket.on('calendar_update', (data) => {
      console.log('📅 Calendar update received:', data)
      this.emit('calendarUpdate', data)

      // Trigger global calendar refresh
      window.dispatchEvent(new CustomEvent('calendar-refresh', { detail: data }))
    })

    this.socket.on('unread_count', (data) => {
      console.log('📊 Unread count updated:', data.count)
      this.emit('unreadCount', data.count)
    })

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Disconnected from notification service:', reason)
      this.isConnected = false
      this.emit('disconnected', reason)
    })

    this.socket.on('connect_error', (error) => {
      console.error('❌ Connection error:', error.message || error)
      this.reconnectAttempts++

      // Stop reconnecting if we hit invalid namespace error
      if (error.message?.includes('Invalid namespace') || error.message?.includes('400')) {
        console.warn('⚠️ WebSocket namespace not available, disabling reconnection')
        this.maxReconnectAttempts = 0
        this.disconnect()
      }

      this.emit('error', error)
    })

    this.socket.on('error', (error) => {
      console.error('❌ Socket error:', error.message || error)
    })

    this.socket.on('pong', () => {
      // Connection is alive
    })
  }

  startPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
    }

    this.pingInterval = setInterval(() => {
      if (this.socket && this.isConnected) {
        this.socket.emit('ping')
      }
    }, 30000) // Ping every 30 seconds
  }

  disconnect() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }

    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }

    this.isConnected = false
    console.log('🔌 Disconnected from notification service')
  }

  // Event emitter methods
  on(event, callback) {
    if (this.callbacks[event]) {
      this.callbacks[event].push(callback)
    }
  }

  off(event, callback) {
    if (this.callbacks[event]) {
      const index = this.callbacks[event].indexOf(callback)
      if (index > -1) {
        this.callbacks[event].splice(index, 1)
      }
    }
  }

  emit(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event].forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error('Error in notification callback:', error)
        }
      })
    }
  }

  // Mark notification as read
  markAsRead(notificationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('mark_notification_read', { notificationId })
    }
  }

  // Browser notification support
  async requestBrowserNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    }
    return false
  }

  showBrowserNotification(notification) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const browserNotification = new Notification(notification.title, {
        body: notification.message,
        icon: `/icons/${notification.icon || 'bell'}.svg`,
        badge: '/icons/hr-badge.png',
        tag: `notification-${notification.id}`,
        requireInteraction: notification.priority === 'urgent',
        timestamp: new Date(notification.createdAt).getTime()
      })

      browserNotification.onclick = () => {
        window.focus()
        this.markAsRead(notification.id)
        browserNotification.close()
      }

      // Auto close after 5 seconds for non-urgent notifications
      if (notification.priority !== 'urgent') {
        setTimeout(() => {
          browserNotification.close()
        }, 5000)
      }
    }
  }

  // API methods for notification management
  async getNotifications({ page = 1, limit = 20, unread_only = false } = {}) {
    try {
      const offset = (page - 1) * limit
      const response = await api.get('/notification-center', {
        params: { limit, offset, unread_only: unread_only ? 'true' : 'false' }
      })
      const data = response.data
      return {
        data: data.notifications || [],
        pagination: {
          total: data.total || 0,
          page,
          totalPages: Math.ceil((data.total || 0) / limit),
          hasPrev: page > 1,
          hasNext: page * limit < (data.total || 0)
        },
        unread_count: data.unread_count || 0
      }
    } catch (error) {
      if (error.silent || error.__CANCEL__) {
        return { data: [], pagination: {}, unread_count: 0 }
      }
      console.error('Error fetching notifications:', error)
      throw new Error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดการแจ้งเตือน')
    }
  }

  async getUnreadCount() {
    try {
      const response = await api.get('/notification-center/unread-count')
      return response.data.unread_count || 0
    } catch (error) {
      console.error('Error fetching unread count:', error)
      return 0
    }
  }

  async markNotificationAsRead(notificationId) {
    try {
      const response = await api.patch(`/notification-center/${notificationId}/read`)
      return response.data
    } catch (error) {
      console.error('Error marking notification as read:', error)
      throw new Error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการอัพเดทการแจ้งเตือน')
    }
  }

  async markAllAsRead() {
    try {
      const response = await api.patch('/notification-center/read-all')
      return response.data
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      throw new Error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการอัพเดทการแจ้งเตือน')
    }
  }

  async deleteNotification(notificationId) {
    try {
      const response = await api.delete(`/notification-center/${notificationId}`)
      return response.data
    } catch (error) {
      console.error('Error deleting notification:', error)
      throw new Error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการลบการแจ้งเตือน')
    }
  }

  async getNotificationStats() {
    try {
      const count = await this.getUnreadCount()
      return { data: { unreadCount: count } }
    } catch (error) {
      console.error('Error fetching notification stats:', error)
      return { data: { unreadCount: 0 } }
    }
  }

  // Test notification (for development)
  async createTestNotification() {
    try {
      const response = await api.post('/notification-center/test')
      return response.data
    } catch (error) {
      console.error('Error creating test notification:', error)
      throw new Error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการสร้างการแจ้งเตือนทดสอบ')
    }
  }

  // Format relative time (same as backend)
  formatRelativeTime(dateTime) {
    const now = new Date()
    const date = new Date(dateTime)
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))

    if (diffInMinutes < 1) return 'เมื่อสักครู่'
    if (diffInMinutes < 60) return `${diffInMinutes} นาทีที่แล้ว`

    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours} ชั่วโมงที่แล้ว`

    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays} วันที่แล้ว`

    if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7)
      return `${weeks} สัปดาห์ที่แล้ว`
    }

    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Get priority class for UI styling
  getPriorityClass(priority) {
    const priorityClasses = {
      'low': 'bg-gray-100 text-gray-600',
      'normal': 'bg-blue-100 text-blue-600',
      'high': 'bg-yellow-100 text-yellow-600',
      'urgent': 'bg-red-100 text-red-600'
    }
    return priorityClasses[priority] || priorityClasses.normal
  }

  // Get category color
  getCategoryColor(category) {
    const categoryColors = {
      'leave': 'blue',
      'attendance': 'green',
      'payroll': 'purple',
      'system': 'gray',
      'hr': 'indigo',
      'admin': 'red'
    }
    return categoryColors[category] || 'blue'
  }
}

export default new NotificationService()